// Audio Capture Service - Browser Microphone to PCM 16kHz Stream

type AudioCaptureState = 'inactive' | 'requesting' | 'active' | 'error';

export type AudioCaptureEventMap = {
  chunk: (data: ArrayBuffer) => void;
  stateChange: (state: AudioCaptureState) => void;
  error: (error: Error) => void;
};

type EventKey = keyof AudioCaptureEventMap;

const TARGET_SAMPLE_RATE = 16_000;

export class AudioCaptureService {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private state: AudioCaptureState = 'inactive';
  private readonly listeners: { [K in EventKey]: AudioCaptureEventMap[K][] } = {
    chunk: [],
    stateChange: [],
    error: [],
  };

  on<K extends EventKey>(event: K, callback: AudioCaptureEventMap[K]): void {
    this.listeners[event].push(callback);
  }

  off<K extends EventKey>(event: K, callback: AudioCaptureEventMap[K]): void {
    const idx = this.listeners[event].indexOf(callback);
    if (idx !== -1) {
      this.listeners[event].splice(idx, 1);
    }
  }

  private emit<K extends EventKey>(event: K, ...args: Parameters<AudioCaptureEventMap[K]>): void {
    for (const listener of this.listeners[event]) {
      (listener as (...a: unknown[]) => void)(...args);
    }
  }

  get currentState(): AudioCaptureState {
    return this.state;
  }

  async start(): Promise<void> {
    if (this.state === 'active') return;

    this.setState('requesting');

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Mikrofon nicht verfuegbar');
      this.setState('error');
      this.emit('error', error);
      throw error;
    }

    try {
      this.audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    } catch {
      // Fallback: create with default sample rate, will downsample
      this.audioContext = new AudioContext();
    }

    this.source = this.audioContext.createMediaStreamSource(this.stream);

    // Buffer size 4096: ~256ms at 16kHz, ~85ms at 48kHz
    const bufferSize = 4096;
    this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);

    const nativeSampleRate = this.audioContext.sampleRate;
    const needsResample = nativeSampleRate !== TARGET_SAMPLE_RATE;

    this.processor.onaudioprocess = (event: AudioProcessingEvent) => {
      const input = event.inputBuffer.getChannelData(0);

      let samples: Float32Array;
      if (needsResample) {
        samples = this.downsample(input, nativeSampleRate, TARGET_SAMPLE_RATE);
      } else {
        samples = input;
      }

      // Convert Float32 to Int16 PCM
      const pcm = new Int16Array(samples.length);
      for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }

      this.emit('chunk', pcm.buffer);
    };

    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);

    this.setState('active');
  }

  stop(): void {
    if (this.processor) {
      this.processor.disconnect();
      this.processor.onaudioprocess = null;
      this.processor = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }

    this.setState('inactive');
  }

  private setState(newState: AudioCaptureState): void {
    if (this.state === newState) return;
    this.state = newState;
    this.emit('stateChange', newState);
  }

  private downsample(
    input: Float32Array,
    fromRate: number,
    toRate: number,
  ): Float32Array {
    const ratio = fromRate / toRate;
    const outputLength = Math.floor(input.length / ratio);
    const output = new Float32Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
      output[i] = input[Math.floor(i * ratio)];
    }

    return output;
  }
}
